import Card from '../../components/card/Card';
import Icon from '../../components/icon/Icon';
import P from '../../components/texts/p/P';
import { legalEntities } from '../../mocks/LegalEntities';
import Input from '../../components/input/Input';
import Button from '../../components/button/Button';
import { useEffect, useRef, useState } from 'react';
import {
  deleteClientDocument,
  downloadClientDocument,
  downloadApplicationDocument,
  downloadLufeCloudFile,
  getLufeCloudFiles,
  saveClientDocument,
  saveApplicationDocument,
} from '../../services/ClientService';
import { deleteApplicationFile } from '../../services/ApplicationService';
import { getOtherLegalEntitiesFileTypes } from '../../services/ApplicationService';
import Span from '../../components/texts/span/Span';
import { cloudDocument, documentData } from '../../types/interfaces';
import {
  ACCEPTED_FORMATS,
  APPLICATION_STATUS,
  MAX_FILE_SIZE,
} from '../../utils/Constants';
import Spinner from '../../components/Spinner/spinner';
import Label from '../../components/texts/label/Label';

const BALANCE_DOCUMENT_ID = '009';

import DropDown from '../../components/dropdown/DropDown';
import { Toast, useToast } from '../../components/toast/toast';
import useStatus from '../../hooks/useStatus';
import { getMimeType, removeFileExtension } from '../../utils/Utils';
import { reconcileDocuments } from '../../utils/documentMerge';
import { LocalDocument, normalizeFilename } from '../../utils/documentModel';
import BulkDocumentUploader from '../../components/bulkDocumentUploader';
import { FileWithType } from '../../components/bulkDocumentUploader/BulkDocumentUploader.types';
import {
  FileRowContainer,
  DropdownWrapper,
  FileNameWrapper,
} from '../../components/bulkDocumentUploader/BulkDocumentUploader.styles';

interface DocumentProps {
  documents: documentData[];
  onSave: Function;
  selectedLegalEntity: string;
  cuit: string;
  applicationId?: string;
  onPreviousClick: Function;
  isSaving: boolean;
  applicationStatus: number;
}

const Documents: React.FC<DocumentProps> = ({
  onSave,
  selectedLegalEntity,
  cuit,
  applicationId,
  documents,
  onPreviousClick,
  isSaving,
  applicationStatus,
}) => {
  const { isApproved, isRejected, isInCommittee, isFinished } = useStatus({
    applicationStatus,
  });
  const [files, setFiles] = useState<(documentData | null)[]>(
    documents.filter((doc) => doc.is_basic_data === true)
  );
  const [fileIds, setFileIds] = useState<string[]>(() =>
    documents
      .filter((doc) => doc.is_basic_data === true)
      .map((doc) => doc.file_name)
  );
  const [errors, setErrors] = useState<string[]>([]);
  const [extraFiles, setExtraFiles] = useState<LocalDocument[]>([]);
  const [extraFilesErrors, setExtraFilesErrors] = useState<string[]>([]);
  const [isContinueDisabled, setIsContinueDisabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [cloudFiles, setCloudFiles] = useState<cloudDocument[]>([]);
  const [bulkUploadedFiles, setBulkUploadedFiles] = useState<FileWithType[]>(
    []
  );
  const [isUploadingPending, setIsUploadingPending] = useState<boolean>(false);
  const [deletedKeys, setDeletedKeys] = useState<string[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const prevSignature = useRef<string>('');
  const isHydrating = useRef<boolean>(false);
  const [otherLegalEntities, setOtherLegalEntities] = useState<Array<{id: number; code: string; description: string}>>([]);

  const generateId = () =>
    `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  // Fetch de tipos de documentos al montar el componente
  useEffect(() => {
    const fetchOtherLegalEntities = async () => {
      try {
        const response = await getOtherLegalEntitiesFileTypes();
        if (response.success && response.data) {
          setOtherLegalEntities(response.data);
        } else {
          useToast.error({
            title: 'Ocurrió un error al obtener los tipos de documentos.',
            id: 'file-types-toast',
            description: '',
            closeButton: true,
            autoClose: 5000,
          });
        }
      } catch (error) {
        console.error('Error fetching other legal entities:', error);
        useToast.error({
          title: 'Ocurrió un error al obtener los tipos de documentos.',
          id: 'file-types-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
      }
    };

    fetchOtherLegalEntities();
  }, []);

  useEffect(() => {
    if (
      applicationStatus === APPLICATION_STATUS.approved.code ||
      applicationStatus === APPLICATION_STATUS.finished.code ||
      applicationStatus === APPLICATION_STATUS.rejected.code
    ) {
      setIsLoading(false);
      return;
    }

    let isCancelled = false;

    const fetchCloudFiles = async () => {
      try {
        const response = await getLufeCloudFiles(cuit);
        if (!response.success || isCancelled) {
          if (!isCancelled) {
            useToast.error({
              title: 'Ocurrió un error al obtener los documentos.',
              id: 'save-toast',
              description: '',
              closeButton: true,
              autoClose: 5000,
            });
          }
          return;
        }

        const requiredIds =
          legalEntities
            .find((item) => item.label === selectedLegalEntity)
            ?.requiredDocuments.map((doc) => doc.id) || [];

        setFiles((prev) => {
          const updated = [...prev];
          let hasNew = false;

          (response.data || []).forEach((doc) => {
            const isRequired = requiredIds.includes(doc.type);
            const alreadyInFiles = prev.some((f) => f?.type === doc.type);

            if (isRequired && !alreadyInFiles) {
              updated.push({
                file: doc.documentId,
                file_name: doc.documentId,
                type: doc.type,
                is_basic_data: true,
              } as any);
              hasNew = true;
            }
          });

          return hasNew ? updated : prev;
        });

        if (!isCancelled) {
          setCloudFiles(response.data || []);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchCloudFiles();

    return () => {
      isCancelled = true;
    };
  }, [applicationStatus, cuit, selectedLegalEntity]);

  // Reconstruir el estado local de documentos (extraFiles) desde backend + locales
  useEffect(() => {
    isHydrating.current = true;

    const base: LocalDocument[] = [
      ...(documents || [])
        .filter((doc) => doc.type && doc.file_name && !doc.is_basic_data) // Excluir documentos básicos para evitar duplicados
        .map((doc, idx) => {
          const baseName = normalizeFilename(doc.file_name || '');
          const isDeleted = deletedKeys.some((k) => normalizeFilename(k) === baseName);
          return {
            id: doc.file_name || doc.type || `${idx}`,
            type: doc.type || '',
            label: otherLegalEntities.find((o) => o.code === doc.type)?.description || '',
            asset_file_path: doc.file_name,
            file_name: doc.file_name,
            isPending: false,
            isDeleted,
            order: idx,
          } as LocalDocument;
        }),
    ];

    setExtraFiles((prev) =>
      reconcileDocuments({
        base,
        locals: prev,
      })
    );
  }, [documents, deletedKeys]);

  const handleFileDelete = (index: string) => {
    const filename = files.find((item) => item?.type === index)?.file_name;
    const fileIndex = files.findIndex((item) => item?.type === index);
    if (filename) {
      const encoded = encodeURIComponent(filename);
      if (applicationId) {
        deleteApplicationFile(cuit, applicationId, encoded, index);
      } else {
        deleteClientDocument(cuit, filename);
      }
      files[fileIndex] = null;
      const newErrors = [...errors];
      newErrors[fileIndex] = null;
      setErrors(newErrors);
      setHasUnsavedChanges(true);
    }
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    documentId: string
  ) => {
    setIsContinueDisabled(true);
    const file = event.target.files?.[0] || null;
    let errorMessage: string = '';
    const newErrors = [...errors];
    if (file) {
      if (documentId === BALANCE_DOCUMENT_ID) {
        const isPdf =
          file.type === 'application/pdf' ||
          file.name.toLowerCase().endsWith('.pdf');
        if (!isPdf) {
          errorMessage =
            'El Último Estado Contable solo acepta archivos PDF.';
        }
      } else if (!ACCEPTED_FORMATS.includes(file.type)) {
        errorMessage =
          'El formato de archivo solo puede ser: .pdf, .jpg, .jpeg, .png, .xls, .xlsx, .doc, .docx';
      }
      if (!errorMessage && file.size > MAX_FILE_SIZE) {
        errorMessage = 'El tamaño del archivo no puede ser mayor de 20 MB';
      }
      const errorIndex =
        legalEntities
          .find((item) => item.label === selectedLegalEntity)
          ?.requiredDocuments.findIndex((doc) => doc.id === documentId) ?? -1;

      if (errorMessage) {
        const inputElement = document.getElementById(
          `file-input-${documentId}`
        ) as HTMLInputElement;
        inputElement.value = '';
        if (errorIndex !== -1) {
          newErrors[errorIndex] = errorMessage;
        }
      } else {
        newErrors[errorIndex] = '';
        const fileName = removeFileExtension(file.name);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', documentId);
        formData.append('fileName', fileName);
        formData.append('isBasicData', 'true');
        const savePromise = applicationId
          ? saveApplicationDocument(cuit, applicationId, formData)
          : saveClientDocument(cuit, formData);
        savePromise.then((response) => {
          if (response.success) {
            const fileIdsArray = [...fileIds];
            fileIdsArray.push(response.data);
            setFileIds(fileIdsArray);
          }
          const newFiles = [...files];
          const newErrors = [...errors];
          const fileToSend = {
            file: file?.name,
            file_name: response.data,
            type: documentId,
            is_basic_data: true,
          };
          const fileIndex = files.findIndex(
            (item) => item?.type === documentId
          );

          if (fileIndex === -1) {
            newFiles.push(fileToSend);
            newErrors.push('');
          } else {
            newFiles[fileIndex] = fileToSend;
            newErrors[fileIndex] = '';
          }
          if (errorIndex !== -1) {
            newErrors[errorIndex] = '';
          }
          setFiles(newFiles);
          setErrors(newErrors);
          setIsContinueDisabled(false);
        });
      }
      setErrors(newErrors);
    }
  };

  const handleDownloadFile = (index: string) => {
    const filename = files.find((item) => item?.type === index)?.file_name;
    if (!filename?.includes('.')) {
      downloadCloudDocument(filename);
    } else {
      const downloadPromise = applicationId
        ? downloadApplicationDocument(cuit, applicationId, filename)
        : downloadClientDocument(cuit, filename);
      
      downloadPromise.then((response) => {
        if (response.success) {
          const mimeType = getMimeType(filename);
          const fileBlob = new Blob([response.data], { type: mimeType });
          const url = window.URL.createObjectURL(fileBlob);

          if (
            mimeType.startsWith('application/pdf') ||
            mimeType.startsWith('image/')
          ) {
            window.open(url, '_blank');
          } else {
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
          }
        }
      });
    }
  };

  const downloadCloudDocument = (url: string) => {
    downloadLufeCloudFile(cuit, url).then((response) => {
      if (response.success) {
        const fileBlob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(fileBlob);
        window.open(url, '_blank');
      }
    });
  };

  const handleExtraFileChange = (file: File) => {
    setIsContinueDisabled(true);
    const id = generateId();
    const newErrors = [...extraFilesErrors, 'El tipo de documento es requerido'];
    setExtraFilesErrors(newErrors);

    const newDoc: LocalDocument = {
      id,
      type: '',
      label: '',
      file_name: file?.name,
      asset_file_path: file?.name,
      file,
      isPending: true,
      isDeleted: false,
      order: extraFiles.length ? Math.max(...extraFiles.map((f) => f.order || 0)) + 1 : 0,
    };

    setExtraFiles((prev) => [...prev, newDoc]);
  };

  const isApplicationEditable = () => {
    return !isApproved && !isFinished && !isRejected && !isInCommittee;
  };

  const handleEntityChange = (e: string, index: number) => {
    const newLegalEntityId = otherLegalEntities.find(
      (item) => item.description === e
    )?.code;

    const newFiles = [...extraFiles];
    const newErrors = [...extraFilesErrors];

    newErrors[index] = null;
    setExtraFilesErrors(newErrors);

    newFiles[index] = {
      ...(newFiles[index] as any),
      type: newLegalEntityId,
    };
    setExtraFiles(newFiles);

    const hasFilesWithoutType = newFiles.some((f) => (f as any)?.type === null);
    setIsContinueDisabled(hasFilesWithoutType);
  };

  const handleExtraFileDelete = (index: number) => {
    const doc = extraFiles[index];

    // Si es pendiente, solo quitar local; si no, marcar como eliminado y limpiar bases
    const isPending =
      doc?.isPending ||
      doc?.file?.name?.toString().startsWith('temp-') ||
      (!doc?.asset_file_path && doc?.file);

    if (!isPending && doc?.asset_file_path) {
      const filename = doc.asset_file_path.split('/').pop() || doc.asset_file_path;
      const encoded = encodeURIComponent(filename);
      if (applicationId) {
        deleteApplicationFile(cuit, applicationId, encoded, doc.type);
      } else {
        deleteClientDocument(cuit, filename);
      }
      setDeletedKeys((prev) => [...prev, doc.asset_file_path]);
    }

    const newFiles = extraFiles.filter((_, i) => i !== index);
    const newErrors = extraFilesErrors.filter((_, i) => i !== index);
    setExtraFiles(newFiles);
    setExtraFilesErrors(newErrors);

    const hasFilesWithoutType = newFiles.some((f) => !f.type);
    setIsContinueDisabled(hasFilesWithoutType);
    setHasUnsavedChanges(true);
  };

  // Custom uploader: no pega al backend, devuelve nombre temporal
  const handleBulkFileUpload = async (
    file: File,
    type: string,
    label: string
  ): Promise<{ success: boolean; data?: string; message?: string }> => {
    const fileName = removeFileExtension(file.name);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const tempFileName = `temp-${type || '000'}-${timestamp}-${fileName}`;

    return {
      success: true,
      data: tempFileName,
    };
  };

  const handleBulkFilesUploaded = (uploadedFiles: FileWithType[]) => {
    setBulkUploadedFiles(uploadedFiles);

    // Reflejar en la lista de extraFiles los archivos ya cargados con etiqueta
    const successfulFiles = uploadedFiles.filter(
      (f) => f.uploadStatus === 'success' && f.type
    );

    if (successfulFiles.length > 0) {
      setExtraFiles((prev) =>
        reconcileDocuments({
          base: [],
          locals: prev,
          updates: successfulFiles.map((f, idx) => ({
            id: f.id,
            file: f.file,
            file_name: f.uploadedFileName || f.fileName,
            asset_file_path: f.uploadedFileName || f.fileName,
            type: f.type!,
            label: f.label || otherLegalEntities.find((o) => o.code === f.type)?.description || '',
            isPending: true,
            isDeleted: false,
            order: prev.length + idx,
          })) as LocalDocument[],
        })
      );

      // Ajustar array de errores al largo final
      setExtraFilesErrors((prev) => {
        const targetLength = Math.max(prev.length, extraFiles.length + successfulFiles.length);
        const next = [...prev];
        while (next.length < targetLength) next.push('');
        return next;
      });
    }

    // Validar si hay archivos sin tipo asignado (excluyendo errores)
    const filesPendingType = uploadedFiles.filter(
      (f) => f.uploadStatus !== 'error' && !f.type
    );

    setIsContinueDisabled(filesPendingType.length > 0);
    setHasUnsavedChanges(true);
  };

  const uploadPendingExtraFiles = async (): Promise<LocalDocument[]> => {
    const updated = [...extraFiles];
    for (let i = 0; i < updated.length; i++) {
      const doc = updated[i];
      if (!doc.isPending || doc.isDeleted) continue;
      if (!doc.file || !doc.type) continue;

      const formData = new FormData();
      formData.append('file', doc.file);
      formData.append('type', doc.type);
      formData.append('fileName', removeFileExtension(doc.file_name || doc.file.name));
      formData.append('isBasicData', 'false');

      const response = applicationId
        ? await saveApplicationDocument(cuit, applicationId, formData)
        : await saveClientDocument(cuit, formData);
      if (response.success && response.data) {
        updated[i] = {
          ...doc,
          id: response.data, // alinear clave con backend para evitar duplicados en la hidratación
          isPending: false,
          asset_file_path: response.data,
          file_name: response.data,
        };
      }
    }
    setExtraFiles(updated);
    return updated;
  };

  const getFilesToSave = (currentExtraFiles = extraFiles) => {
    const normalizeKey = (type?: string | null, name?: string | null) => {
      const base = (name || '').split('/').pop() || '';
      const withoutCuit = base.match(/^\d{11}-.+/) ? base.replace(/^\d{11}-/, '') : base;
      // Quitar timestamp final -dd-mm-yyyyThh-mm-ss (antes de sacar extensión)
      const withoutTs = withoutCuit.replace(
        /(.*)-\d{2}-\d{2}-\d{4}T\d{2}-\d{2}-\d{2}(\.[^.]+)?$/,
        '$1'
      );
      const withoutExt =
        withoutTs.includes('.') && withoutTs.lastIndexOf('.') > 0
          ? withoutTs.substring(0, withoutTs.lastIndexOf('.'))
          : withoutTs;
      return `${type || ''}::${withoutExt.toLowerCase()}`;
    };

    const toDocumentData = (doc: any, forceBasic?: boolean): documentData | null => {
      const type = doc?.type;
      const fileName = doc?.asset_file_path || doc?.file_name;
      if (!type || !fileName) return null;
      return {
        file: fileName,
        file_name: fileName,
        type: String(type),
        is_basic_data: forceBasic ?? !!doc?.is_basic_data,
      };
    };

    const isValidExtra = (f: any) =>
      f &&
      !f.isDeleted &&
      f.type &&
      (f.asset_file_path ||
        f.file_name ||
        f.file);

    // Extras limpios (no borrados, con tipo y nombre)
    const cleanExtraFiles = currentExtraFiles.filter(isValidExtra);

    // Archivos subidos por bulk listos para payload
    const bulkUploadedFilesData: documentData[] = bulkUploadedFiles
      .filter((f) => f.uploadStatus === 'success' && f.uploadedFileName && f.type)
      .map((f) => ({
        file: f.uploadedFileName || f.fileName,
        file_name: f.uploadedFileName || f.fileName,
        type: f.type || '',
        is_basic_data: false,
      }));

    // Base (requeridos) provenientes de estado files
    const baseDocs: documentData[] = files
      .filter(Boolean)
      .map((f) => toDocumentData(f, f?.is_basic_data ?? true))
      .filter((d): d is documentData => Boolean(d));

    // Extras locales (incluye temp y renombrados)
    const extraDocs: documentData[] = cleanExtraFiles
      .map((f) => toDocumentData(f, false))
      .filter((d): d is documentData => Boolean(d));

    const candidates: documentData[] = [
      ...baseDocs,
      ...extraDocs,
      ...bulkUploadedFilesData,
    ];

    const dedup = new Map<string, documentData>();
    const isTemp = (name: string) => name.startsWith('temp-');

    candidates.forEach((doc) => {
      const key = normalizeKey(doc.type, doc.file_name);
      const existing = dedup.get(key);
      if (!existing) {
        dedup.set(key, doc);
        return;
      }
      
      // Prioridad 1: Preferir archivos con is_basic_data: true sobre is_basic_data: false
      // Si el existente es básico y el nuevo no, mantener el existente
      if (existing.is_basic_data && !doc.is_basic_data) {
        return; // Mantener el existente
      }
      // Si el nuevo es básico y el existente no, reemplazar
      if (!existing.is_basic_data && doc.is_basic_data) {
        dedup.set(key, doc);
        return;
      }
      
      // Prioridad 2: Preferir el que no sea temp si ya existe uno temp
      const existingTemp = isTemp(existing.file_name);
      const currentTemp = isTemp(doc.file_name);
      if (existingTemp && !currentTemp) {
        dedup.set(key, doc);
        return;
      }
      
      // Si ambos tienen el mismo is_basic_data y ambos son temp o ambos no son temp,
      // mantener el existente para evitar duplicados
    });

    return Array.from(dedup.values());
  };

  const handleSubmit = async (shouldContinue: boolean) => {
    // Validar tipos pendientes
    const missingType =
      extraFiles.some((f) => !f.type && !f.isDeleted) ||
      bulkUploadedFiles.some(
        (f) => f.uploadStatus !== 'error' && (!f.type || f.type === null)
      );
    if (missingType) {
      setIsContinueDisabled(true);
      return;
    }

    try {
      setIsUploadingPending(true);
      const updatedExtra = await uploadPendingExtraFiles();
      const filesToSave = getFilesToSave(updatedExtra as any);
      onSave({ files: filesToSave, shouldContinue });
      setHasUnsavedChanges(false);
    } finally {
      setIsUploadingPending(false);
    }
  };

  return (
    <Card spacing={{ left: '6' }}>
      <Toast id='documents-toast' />
      <Card display='grid' gap='8px' margin={{ bottom: '4' }}>
        <P
          type='1'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
        >
          Cargar documentación básica
        </P>
        <P type='1' withMargin={false} variant='neutral' modifier='regular'>
          La documentación pasará por un sistema de verificación antes de ser
          aprobada.
        </P>
      </Card>
      {isLoading ? (
        <Card
          margin='3'
          display='flex'
          justifyContent='center'
          height='200px'
          alignItems='center'
        >
          <Spinner size='lg' variant='primary' />
        </Card>
      ) : (
        <Card margin={{ top: '5', bottom: '5' }} width='auto' spacing={'3'}>
          {legalEntities
            .find((item) => item.label === selectedLegalEntity)
            ?.requiredDocuments.map((document, index) => (
              <Card
                margin={{ bottom: '3' }}
                display='flex'
                alignItems='center'
                width='auto'
                gap='16px'
              >
                <Icon variant='neutral' name='document_outline' />
                <Card width='25%'>
                  <P
                    type='3'
                    withMargin={false}
                    variant='neutral'
                    modifier='strong'
                  >
                    {document.value}
                  </P>
                </Card>
                <Card width='240px'>
                  {applicationStatus !== APPLICATION_STATUS.approved.code &&
                    applicationStatus !== APPLICATION_STATUS.finished.code &&
                    applicationStatus !== APPLICATION_STATUS.rejected.code &&
                    cloudFiles.find((file) => file.type === document.id) && (
                      <Button
                        id={`documents-lufe-button-${index}`}
                        variant='secondary'
                        icon='cloud_outline'
                        iconAlign='right'
                        onClick={() =>
                          downloadCloudDocument(
                            cloudFiles.findLast(
                              (file) => file.type === document.id
                            ).documentId
                          )
                        }
                        fullWidth
                        size='lg'
                      >
                        Buscar en la nube
                      </Button>
                    )}
                </Card>
                <Card width='30%'>
                  {files.find((item) => item?.type === document.id)?.type ? (
                    <Card
                      display='flex'
                      alignItems='center'
                      gap='10px'
                      width='420px'
                    >
                      <Card
                        width='300px'
                        variant='neutral'
                        modifier='inverted'
                        height='46px'
                        spacing={{ left: '1', right: '1' }}
                        borderRadiusAmount='3'
                        border
                        display='flex'
                      >
                        <Label variant='neutral' modifier='strong' type='2'>
                          {files
                            .find((item) => item?.type === document.id)
                            ?.file_name.includes('.')
                            ? files.find((item) => item?.type === document.id)
                                ?.file_name
                            : 'Documento asignado desde LUFE'}
                        </Label>
                      </Card>
                      <Button
                        variant='secondary'
                        size='md'
                        onClick={() => handleDownloadFile(document.id)}
                      >
                        <Icon
                          name='view_outline'
                          variant='neutral'
                          height={44}
                          cursor='pointer'
                        />
                      </Button>
                      <Button
                        variant='secondary'
                        size='md'
                        onClick={() => handleFileDelete(document.id)}
                        disabled={!isApplicationEditable()}
                      >
                        <Icon
                          name='delete_outline'
                          variant='neutral'
                          height={44}
                          cursor='pointer'
                        />
                      </Button>
                    </Card>
                  ) : (
                    <Input
                      id={`file-input-${document.id}`}
                      type='file'
                      size='md'
                      icons={{ iconRight: 'upload_outline' }}
                      onChange={(event) => handleFileChange(event, document.id)}
                      accept={
                        document.id === BALANCE_DOCUMENT_ID
                          ? '.pdf,application/pdf'
                          : '.pdf, .jpg, .jpeg, .png, .xls, .xlsx, .doc, .docx'
                      }
                      disabled={!isApplicationEditable()}
                    />
                  )}
                  {errors[index] && (
                    <Span variant='danger' type='2'>
                      {errors[index]}
                    </Span>
                  )}
                </Card>
              </Card>
            ))}
        </Card>
      )}
      <Card display='grid' gap='8px' margin={{ bottom: '4' }}>
        <P
          type='1'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
        >
          Cargar documentación extra
        </P>
        <P type='1' withMargin={false} variant='neutral' modifier='regular'>
          La documentación pasará por un sistema de verificación antes de ser
          aprobada.
        </P>
      </Card>
      {/* Mostrar archivos extra existentes ARRIBA del drag and drop */}
      {extraFiles.map((document, index) => (
        <Card key={`extra-file-${index}`}>
          <Card margin={{ top: '3' }}>
            <FileRowContainer>
              <Icon variant='neutral' name='document_outline' />
              <DropdownWrapper>
                <DropDown
                  id='client-legal_entity-dropdown'
                  items={otherLegalEntities.map(item => ({ id: item.code, label: item.description }))}
                  size='md'
                  title={
                    otherLegalEntities.find((item) => item.code === document?.type)
                      ?.description
                      ? otherLegalEntities.find(
                          (item) => item.code === document?.type
                        )?.description
                      : 'Seleccioná el tipo de documento'
                  }
                  onChange={(e) => handleEntityChange(e, index)}
                  disabled={!isApplicationEditable()}
                ></DropDown>
              </DropdownWrapper>
              <FileNameWrapper>
                <Card
                  display='flex'
                  justifyContent='space-between'
                  width='100%'
                  variant='neutral'
                  modifier='inverted'
                  height='46px'
                  alignItems='center'
                  spacing={{ left: '2', right: '2' }}
                  borderRadiusAmount='3'
                  border
                  contain='content'
                >
                  <Label variant='neutral' modifier='strong' type='2'>
                    {document?.file_name}
                  </Label>
                  <Icon
                    name='delete_outline'
                    variant='neutral'
                    height={44}
                    cursor='pointer'
                    onClick={() => handleExtraFileDelete(index)}
                    disabled={!isApplicationEditable()}
                  />
                </Card>
              </FileNameWrapper>
            </FileRowContainer>
          </Card>
          <Card margin={{ top: '1', left: '6' }}>
            <Span variant='danger' type='2'>
              {extraFilesErrors[index]}
            </Span>
          </Card>
        </Card>
      ))}
      {extraFiles.length > 0 && (
        <Card margin={{ top: '5' }} />
      )}
      {/* Componente BulkDocumentUploader - Drag & Drop ABAJO */}
      <BulkDocumentUploader
        documentTypes={otherLegalEntities.map(item => ({ id: item.code, label: item.description }))}
        onFilesUploaded={handleBulkFilesUploaded}
        onValidationChange={(hasFilesWithoutType) => {
          if (hasFilesWithoutType) {
            setIsContinueDisabled(true);
          }
        }}
        maxFileSize={MAX_FILE_SIZE}
        acceptedFormats={ACCEPTED_FORMATS}
        disabled={!isApplicationEditable() || isUploadingPending}
        cuit={cuit}
        onFileUpload={handleBulkFileUpload}
      />
      <Card display='flex' justifyContent='space-between' margin={{ top: '5' }}>
        <Card width='200px'>
          <Button
            id='client-button-back'
            size='lg'
            variant='secondary'
            onClick={() => onPreviousClick()}
            fullWidth
          >
            Volver
          </Button>
        </Card>
        <Card display='flex' justifyContent='flex-end' gap='16px' width='420px'>
          <Button
            id='client-button-save'
            size='lg'
            variant='secondary'
            onClick={() => handleSubmit(false)}
            disabled={
              isRejected ||
              isApproved ||
              isFinished ||
              isContinueDisabled ||
              isSaving ||
              isUploadingPending
            }
            fullWidth
          >
            {isSaving ? <Spinner size='sm' variant='primary' /> : 'Guardar'}
          </Button>
          <Button
            id='client-button-save'
            size='lg'
            variant='primary'
            onClick={() => handleSubmit(true)}
            fullWidth
            disabled={isContinueDisabled || isSaving || isUploadingPending}
          >
            {isSaving ? <Spinner size='sm' variant='primary' /> : 'Continuar'}
          </Button>
        </Card>
      </Card>
    </Card>
  );
};

export default Documents;